import React from 'react'
import { connect } from 'react-redux'
import injectSheet from 'react-jss'
import style from './style'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CSSTransition } from 'react-transition-group'
import Fuse from 'fuse.js'
import ManufacturerList from './ManufacturerList'
import ModelList from './ModelList'
import ModelDetails from './ModelDetails'
import ChangeModelWarning from './ChangeModelWarning'
import { changeMainControllerModelAction } from 'actions/entityActions'
import { addRecentModelAction } from 'actions/cameraActions'
import { updateUiAction } from 'actions/uiActions'
import { currentDoorControllerSelector, doorControllerUsagesSelector, recentDoorControllerModelsSelector,
  favoriteModelsSelector } from 'selectors/components'
import { doorControllerDefaults } from 'helpers/accessControl/defaults'
import { RECENT_DISPLAY_COUNT } from 'helpers/globals'
import uniq from 'ramda/src/uniq'
import isEmpty from 'ramda/src/isEmpty'
import complement from 'ramda/src/complement'
import sortBy from 'ramda/src/sortBy'

const mapStateToProps = state => {
  return {
    doorController: currentDoorControllerSelector(state),
    usages: doorControllerUsagesSelector(state),
    doorControllerModels: state.doorControllerModels,
    permalink: state.permalink,
    recentModels: recentDoorControllerModelsSelector(state),
    favoriteModels: favoriteModelsSelector(state, { controllerModel: true })
  }
}

class DoorControllerModelSelector extends React.Component {
  constructor(props) {
    super(props)
    const { doorController: { model: { id } }, doorControllerModels, recentModels } = this.props

    this.anchorRef = null
    this.generic = null
    this.models = doorControllerModels.allIds.map(id => {
      const model = doorControllerModels.byId[id]

      return {
        ...model,
        trimmedText: model.text.replace(/\s|\\|\)|\(|\[|-/g, ''),
        trimmedModel: model.model.replace(/\s|\\|\)|\(|\[|-/g, '')
      }
    })

    const options = {
      threshold: 0.22,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      shouldSort: true,
      includeScore: true,
      keys: [{
        name: 'trimmedModel',
        weight: 0.9
      }, {
        name: 'trimmedText',
        weight: 0.1
      }]
    }
    this.fuse = new Fuse(this.models, options)

    const selectedModel = doorControllerModels.byId[id]
    const selectedManufacturer = selectedModel && selectedModel.manufacturer
    const models = selectedManufacturer ? this.models.filter(m => m.manufacturer === selectedManufacturer) : this.models

    this.state = {
      models: sortBy(m => recentModels.indexOf(m.id) === -1 ? RECENT_DISPLAY_COUNT + 1 : recentModels.indexOf(m.id))(models),
      selectedModel,
      showChangeWarning: false,
      manufacturers: uniq(this.models.map(m => m.manufacturer)),
      selectedManufacturer,
      search: ''
    }
  }

  setModel = selectedModel => this.setState({ selectedModel })

  searchModels = (search, manufacturer) => {
    const { recentModels, favoriteModels } = this.props
    const sanitizedSearch = search.replace(/\s|\\|\)|\(|\[|-/g, '')

    let models = this.models

    if (complement(isEmpty)(sanitizedSearch)) {
      models = this.fuse.search(sanitizedSearch).map(r => r.item)
    }

    // to ensure selecting / deselecting a manufacturer doesn't hide the others, get manufacturers now
    const manufacturers = uniq(models.map(m => m.manufacturer))

    if (manufacturer) {
      if (manufacturer === 'Favorite') {
        models = models.filter(m => favoriteModels.includes(m.id))
      } else if (manufacturer === 'Recent') {
        models = models.filter(m => recentModels.includes(m.id))
      } else {
        models = models.filter(m => m.manufacturer === manufacturer)
      }
    }

    this.setState({
      search,
      models: sortBy(m => recentModels.indexOf(m.id) === -1 ? RECENT_DISPLAY_COUNT + 1 : recentModels.indexOf(m.id))(models),
      selectedModel: null,
      manufacturers,
      selectedManufacturer: manufacturer
    })
  }

  changeModelAction = (model, anchorRef, generic = false) => {
    const { doorController: { id }, usages, changeMainControllerModelAction, addRecentModelAction, updateUiAction } = this.props

    if (Object.keys(usages).some(limitType => usages[limitType] > model.limits[limitType])) {
      this.anchorRef = anchorRef
      this.generic = generic
      return this.setState({ showChangeWarning: true })
    }

    changeMainControllerModelAction(id, model)
    addRecentModelAction(model.id, Date.now(), true)
    updateUiAction({ modal: null })
  }

  confirmWarning = _ => {
    const { closeWarning, state: { selectedModel }, props: { doorController: { id }, changeMainControllerModelAction,
      addRecentModelAction, updateUiAction } } = this

    changeMainControllerModelAction(id, selectedModel, true)
    addRecentModelAction(selectedModel.id, Date.now(), true)
    closeWarning()
    updateUiAction({ modal: null })
  }
  closeWarning = _ => this.setState({ showChangeWarning: false })

  render() {
    const { setModel, changeModelAction, confirmWarning, closeWarning, anchorRef, generic, searchModels,
      state: { models, selectedModel, showChangeWarning, manufacturers, selectedManufacturer, search },
      props: { doorController: { model }, usages, permalink, recentModels, favoriteModels, classes } } = this

    return (
      <div className={classes.modelSelector}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <input
            onChange={({ target: { value } }) => searchModels(value, selectedManufacturer)}
            value={search}
            placeholder={selectedManufacturer ? `Search ${selectedManufacturer}...` : 'Search...'}
            type='text'
            style={{ flex: '0 0 40%' }}
          />
          <button
            onClick={event => changeModelAction(doorControllerDefaults.model, event.target, true)}
            disabled={!model.id}
            className={classes.genericButton}
          >
            <FontAwesomeIcon icon={['far', 'microchip']} />
            Select Generic Controller
          </button>
        </div>
        <div className={classes.columnWrapper}>
          <ManufacturerList
            manufacturers={manufacturers}
            selectedManufacturer={selectedManufacturer}
            setManufacturer={manufacturer => searchModels(search, manufacturer)}
          />
          <ModelList
            models={models}
            selectedModel={selectedModel}
            setModel={setModel}
            usages={usages}
            recentModels={recentModels}
            favoriteModels={favoriteModels}
            search={search}
            selectedManufacturer={selectedManufacturer}
          />
          <ModelDetails
            selectedModel={selectedModel}
            usages={usages}
            changeModelAction={changeModelAction}
            permalink={permalink}
            modelCount={models.length}
          />
        </div>
        <CSSTransition
          in={showChangeWarning}
          timeout={200}
          classNames={{ enter: classes.alertEnter, enterActive: classes.alertEnterActive, exit: classes.alertExit, exitActive: classes.alertExitActive }}
          unmountOnExit
        >
          <ChangeModelWarning
            anchorRef={anchorRef}
            generic={generic}
            confirm={confirmWarning}
            close={closeWarning}
          />
        </CSSTransition>
      </div>
    )
  }
}
export const DoorControllerModelSelectorConfig = { width: '80vw', title: 'Select Controller Model' }
export default connect(mapStateToProps, { changeMainControllerModelAction, addRecentModelAction, updateUiAction })(injectSheet(style)(DoorControllerModelSelector))



// WEBPACK FOOTER //
// ./src/components/DoorControllerModelSelector/index.js